import React, { useEffect } from 'react';
import cn from 'classnames';
import ButtonWithIcon from 'chameleon/packages/blue-book-player/library/button-with-icon';
import BlueBookTooltip from 'chameleon/packages/blue-book-player/library/tooltip/BlueBookTooltip';
import useGetPlayerEl from 'chameleon/packages/blue-book-player/hooks/useGetPlayerEl';
import {
  clearMcqRegionAriaLabel,
  doSetHiddenAnswer,
  setMcqRegionAriaLabel,
  toggleAnswerOptionsCrossOut,
} from 'chameleon/packages/blue-book-player/utils';
import { SELECTORS } from 'chameleon/packages/blue-book-player/constants/selectors';
import styles from './CrossOutToggle.css';

const CrossOutToggle = (props) => {
  const playerEl = useGetPlayerEl();
  const { crossOutReducer, learnosityQuestionApi, question, isStudentAccount } =
    props;
  const [crossOutState, dispatchers] = crossOutReducer;
  const { isEnabled, crossedOutByReferenceCount } = crossOutState;
  const reference = question?.learnosityItemReference;
  const crossedOutCount = crossedOutByReferenceCount?.[reference] || 0;

  const saveCrossOutMainToggleStatus = (
    toggle,
    crossOutCount,
    firstAnswerOptionEl,
  ) => {
    const { getMetadata, setMetadata } = learnosityQuestionApi.response;
    const metadata = getMetadata();

    const saveMainToggleMetadata = () => {
      setMetadata({
        ...(metadata || {}),
        crossOutToggleStatus: toggle,
        crossOutToggleCount: crossOutCount,
      });
    };

    //It is undefined when the current item was never answered
    if (metadata === undefined && !learnosityQuestionApi.isAttempted()) {
      //click first answer option to enable setMetadata
      const undo = doSetHiddenAnswer(firstAnswerOptionEl);
      //Allows the click event to take place, allow learnosity to config the answered question
      setTimeout(() => {
        learnosityQuestionApi.resetResponse();
        undo();
        saveMainToggleMetadata();
      }, 0);
    } else {
      saveMainToggleMetadata();
    }
  };

  useEffect(() => {
    if (crossOutState?.isEnabled && crossOutState?.init) {
      toggleAnswerOptionsCrossOut(playerEl, true);
    }
  }, [crossOutState?.isEnabled && crossOutState?.init]);

  const onClick = (e) => {
    toggleAnswerOptionsCrossOut(playerEl, !isEnabled);
    const crossOutCount = dispatchers.increaseToggleCrossOutCount();
    const firstAnswerOptionEl = e.target
      .closest('.learnosity-item[data-reference]')
      .querySelector(
        `${SELECTORS.OPTION_ANSWER} ${SELECTORS.OPTION_ANSWER_INPUT}`,
      );
    if (isStudentAccount) {
      saveCrossOutMainToggleStatus(
        !isEnabled,
        crossOutCount,
        firstAnswerOptionEl,
      );
    }
    dispatchers.toggleCrossOut();
  };

  useEffect(() => {
    if (isEnabled && reference) {
      dispatchers.resetCrossedOutByRefCount(reference);
    }
  }, [reference, isEnabled]);

  useEffect(() => {
    //initially role=group, updated by setMcqRegionA11y()
    const answersGroupEl = playerEl?.querySelector(
      `.learnosity-item[data-reference="${reference}"] ${SELECTORS.QUESTION_RESPONSES} div[role="region"]`,
    );

    if (isEnabled && answersGroupEl && reference) {
      setMcqRegionAriaLabel(answersGroupEl, crossedOutCount);
    } else if (!isEnabled && answersGroupEl && reference) {
      clearMcqRegionAriaLabel(answersGroupEl);
    }
  }, [reference, isEnabled, playerEl, crossedOutCount]);

  return (
    <div className="flex">
      <BlueBookTooltip
        tooltipProps={{
          id: 'main-crossout-button',
        }}
        description="Cross out answer choices you think are wrong."
      >
        <ButtonWithIcon
          isBlueBookIcon
          icon="crossOut"
          onClick={onClick}
          data-cy="crossout-main-toggle"
          className={cn('active:text-white', {
            [styles['crossout-is-enabled']]: isEnabled,
            [styles['crossout-is-disabled']]: !isEnabled,
          })}
          iconAccessibility={{
            'aria-hidden': true,
          }}
          accessibility={{
            'aria-pressed': isEnabled,
            'aria-label': 'Option eliminator',
            'aria-describedby': 'main-crossout-button',
          }}
        />
      </BlueBookTooltip>
    </div>
  );
};

export default CrossOutToggle;
